import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import { SaveCancelByIconButtons } from './SaveCancelByIconButtons';
import { OverlayGrayBackground } from './OverlayGrayBackground';
import { LoaderSpinner } from './LoaderSpinner';

export const ValueSelectByObject = ({
    options,
    selectClassName,
    selectedValue,
    disabled,
    createNew, 
    onSelect,
    isLoading,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showAddingEDT, setShowAddingEDT] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const containerRef = useRef(null);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    const selectedOption = options.find((opt) => opt.value === selectedValue);

    useEffect(() => {
        if (!isOpen) {
            setShowAddingEDT(false);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleButtonClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen((prev) => !prev);
    };

    return (
        <div className={`relative inline-block ${selectClassName}`} ref={containerRef}>
            {isLoading && (
                <div className="absolute -translate-y-1/2 top-1/2 -left-7">
                    <LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />
                </div>
            )}
            <button
                ref={buttonRef}
                disabled={isLoading || disabled}
                onClick={handleButtonClick}
                title={selectedOption ? selectedOption.label : ''}
                className="flex items-center justify-between disabled:bg-gray-200 gap-1 w-full bg-white border border-gray-300 rounded shadow-sm px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
            >
                <span className='text-ellipsis overflow-hidden'>{selectedOption ? selectedOption.label : ''}</span>
                <ChevronDownIcon className="h-4 w-4" />
            </button>
            {isOpen && (
                <div 
                    ref={dropdownRef}
                    className='absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg text-xs z-50'
                >
                    {isLoading && <OverlayGrayBackground />}
                    <div className="max-h-48 overflow-auto">
                        <ul aria-disabled={isLoading}>
                            {options.map((option, index) => (
                                <li
                                    key={`${option.value}-${index}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelect(option);
                                        setIsOpen(false);
                                    }}
                                    className={`cursor-pointer px-2 py-1 hover:bg-gray-100 hover:text-black text-left ${option.value === selectedValue ? "bg-blue-600 text-white" : ""}`}
                                >
                                    {option.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        {!showAddingEDT && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAddingEDT(true);
                                }}
                                className="flex w-full justify-center cursor-pointer px-2 py-1 text-blue-600 hover:bg-blue-50 border-t border-gray-200"
                            >
                                Add new
                            </button>
                        )}
                        {showAddingEDT && (
                            <div
                                onClick={(e) => e.stopPropagation()}
                                className="flex h-[60px] justify-center flex-col cursor-pointer px-1 py-1 text-blue-600 border-gray-200 bg-white border-gray-300 rounded-md shadow-lg"
                            >
                                <input 
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    className='py-0.5 px-1 shadow-sm border border-gray-300 rounded-md shadow-sm text-sm h-auto'
                                />
                                <div className='h-[25px]'>
                                    <SaveCancelByIconButtons
                                        onSaveClick={async () => {
                                            if (inputValue) {
                                                createNew(inputValue);
                                                setInputValue("");
                                            }
                                        }}
                                        onCancelClick={() => setShowAddingEDT(false)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
